import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface Application {
  id: string
  tenant_id: string
  name: string
  ios_bundle_id: string | null
  android_package_name: string | null
  apns_key_id: string | null
  apns_team_id: string | null
  fcm_credentials_encrypted: string | null
  webhook_url: string | null
  created_at: string
  updated_at: string
}

interface PaginatedResponse<T> {
  data: T[]
  meta: {
    page: number
    limit: number
    total: number
  }
}

export function useApplications() {
  return useQuery({
    queryKey: ['applications'],
    queryFn: () => api.get<Application[]>('/dashboard/applications'),
  })
}

export function useApplication(id: string) {
  return useQuery({
    queryKey: ['applications', id],
    queryFn: () => api.get<Application>(`/dashboard/applications/${id}`),
    enabled: !!id,
  })
}

export function useCreateApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      name: string
      ios_bundle_id?: string
      android_package_name?: string
      webhook_url?: string
    }) => {
      return api.post<Application>('/dashboard/applications', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] })
    },
  })
}

export function useUpdateApplication(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      name?: string
      ios_bundle_id?: string | null
      android_package_name?: string | null
      webhook_url?: string | null
    }) => {
      return api.patch<Application>(`/dashboard/applications/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] })
      queryClient.invalidateQueries({ queryKey: ['applications', id] })
    },
  })
}

export function useDeleteApplication(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      return api.delete(`/dashboard/applications/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] })
    },
  })
}

export function useConfigureApns(appId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { keyId: string; teamId: string; key: string }) => {
      return api.post(`/dashboard/applications/${appId}/apns`, {
        keyId: data.keyId,
        teamId: data.teamId,
        key: data.key,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications', appId] })
    },
  })
}

export function useConfigureFcm(appId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { credentials: string }) => {
      return api.post(`/dashboard/applications/${appId}/fcm`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications', appId] })
    },
  })
}
